import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { User, Package, MapPin, Heart, LogOut, Plus, Trash2, Edit2, Check, X as XIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { authGoogle, updateProfile, changePassword, getUserOrders, getAddresses, addAddress, deleteAddress, getWishlist, removeFromWishlist } from '../lib/api';
import ProductCard from '../components/ProductCard';
import { toast } from 'sonner';

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'orders', label: 'Orders', icon: Package },
  { id: 'addresses', label: 'Addresses', icon: MapPin },
  { id: 'wishlist', label: 'Wishlist', icon: Heart },
];

function ProfileTab({ user, refreshUser }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user.name || '', email: user.email || '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '' });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateProfile(form);
      await refreshUser();
      setEditing(false);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setLoading(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await changePassword(pwForm);
      setPwForm({ current_password: '', new_password: '' });
      toast.success('Password updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="profile-tab">
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Account Details</h2>
          {!editing && <Button variant="ghost" size="sm" onClick={() => setEditing(true)}><Edit2 size={16} className="mr-1" /> Edit</Button>}
        </div>
        {editing ? (
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" data-testid="profile-name-input" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1" data-testid="profile-email-input" /></div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={loading} className="bg-[#003366]" data-testid="profile-save-btn"><Check size={16} className="mr-1" /> Save</Button>
              <Button variant="outline" onClick={() => setEditing(false)}><XIcon size={16} className="mr-1" /> Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-12 h-12 rounded-full" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#003366] flex items-center justify-center text-white text-lg font-semibold">{user.name?.[0]?.toUpperCase() || '?'}</div>
              )}
              <div>
                <p className="font-medium text-slate-900" data-testid="profile-name-display">{user.name}</p>
                <p className="text-sm text-slate-500">{user.email}</p>
              </div>
            </div>
            <div className="text-sm text-slate-500">
              <p>Signed in via {user.auth_provider === 'google' ? 'Google' : 'Email'}</p>
              <p>Member since {new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
        )}
      </div>

      {user.auth_provider !== 'google' && (
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-4">Change Password</h2>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div><Label>Current Password</Label><Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm(p => ({ ...p, current_password: e.target.value }))} className="mt-1" /></div>
            <div><Label>New Password</Label><Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm(p => ({ ...p, new_password: e.target.value }))} placeholder="Min. 6 characters" className="mt-1" /></div>
            <Button type="submit" disabled={loading} className="bg-[#003366]">Update Password</Button>
          </form>
        </div>
      )}
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getUserOrders().then(r => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-8 text-slate-500">Loading orders...</div>;
  if (!orders.length) return (
    <div className="text-center py-12 bg-white rounded-lg border" data-testid="orders-tab">
      <Package className="mx-auto text-slate-300 mb-3" size={48} />
      <p className="text-slate-500 mb-4">No orders yet</p>
      <Link to="/products"><Button className="bg-[#003366]">Start Shopping</Button></Link>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="orders-tab">
      {orders.map(order => (
        <div key={order.id} className="bg-white rounded-lg border p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
            <div>
              <span className="font-semibold text-slate-900">Order #{order.id.slice(0, 8).toUpperCase()}</span>
              <span className="text-sm text-slate-500 ml-2">{new Date(order.created_at).toLocaleDateString()}</span>
            </div>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
              order.status === 'confirmed' || order.status === 'shipped' ? 'bg-green-100 text-green-700' :
              order.status === 'payment_failed' ? 'bg-red-100 text-red-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>{order.status?.replace('_', ' ')}</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <span>{order.items?.length || 0} item(s)</span>
            <span className="font-medium text-slate-900">${order.total?.toFixed(2)}</span>
          </div>
          <Link to={`/track-order?order_id=${order.id}`} className="text-sm text-[#003366] hover:underline mt-2 inline-block">Track Order</Link>
        </div>
      ))}
    </div>
  );
}

function AddressesTab() {
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: 'Home', address: '', city: '', state: '', zipCode: '', country: 'United States', is_default: false });

  const fetchAddresses = useCallback(() => {
    getAddresses().then(r => setAddresses(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchAddresses(); }, [fetchAddresses]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await addAddress(form);
      setShowForm(false);
      setForm({ label: 'Home', address: '', city: '', state: '', zipCode: '', country: 'United States', is_default: false });
      fetchAddresses();
      toast.success('Address added');
    } catch (err) { toast.error('Failed to add address'); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAddress(id);
      fetchAddresses();
      toast.success('Address removed');
    } catch { toast.error('Failed to remove'); }
  };

  return (
    <div className="space-y-4" data-testid="addresses-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Saved Addresses</h2>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[#003366]" data-testid="add-address-btn">
          <Plus size={16} className="mr-1" /> Add Address
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="bg-white rounded-lg border p-5 space-y-3" data-testid="address-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Label</Label><Input value={form.label} onChange={(e) => setForm(p => ({ ...p, label: e.target.value }))} className="mt-1" placeholder="Home, Work..." data-testid="address-label-input" /></div>
            <div className="col-span-2"><Label>Street Address</Label><Input value={form.address} onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))} className="mt-1" data-testid="address-street-input" required /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))} className="mt-1" required /></div>
            <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm(p => ({ ...p, state: e.target.value }))} className="mt-1" required /></div>
            <div><Label>ZIP Code</Label><Input value={form.zipCode} onChange={(e) => setForm(p => ({ ...p, zipCode: e.target.value }))} className="mt-1" required /></div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={form.is_default} onChange={(e) => setForm(p => ({ ...p, is_default: e.target.checked }))} />
            Set as default address
          </label>
          <div className="flex gap-2">
            <Button type="submit" className="bg-[#003366]">Save Address</Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </form>
      )}

      {!addresses.length && !showForm && (
        <div className="text-center py-8 bg-white rounded-lg border">
          <MapPin className="mx-auto text-slate-300 mb-3" size={48} />
          <p className="text-slate-500">No saved addresses yet</p>
        </div>
      )}

      {addresses.map(addr => (
        <div key={addr.id} className="bg-white rounded-lg border p-4 flex items-start justify-between" data-testid={`address-${addr.id}`}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-slate-900">{addr.label}</span>
              {addr.is_default && <span className="text-xs bg-[#003366]/10 text-[#003366] px-2 py-0.5 rounded-full font-medium">Default</span>}
            </div>
            <p className="text-sm text-slate-600">{addr.address}, {addr.city}, {addr.state} {addr.zipCode}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(addr.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></Button>
        </div>
      ))}
    </div>
  );
}

function WishlistTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWishlist = useCallback(() => {
    setLoading(true);
    getWishlist().then(r => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchWishlist(); }, [fetchWishlist]);

  const handleRemove = async (productId) => {
    try {
      await removeFromWishlist(productId);
      setProducts(prev => prev.filter(p => p.id !== productId));
      toast.success('Removed from wishlist');
    } catch { toast.error('Failed to remove'); }
  };

  if (loading) return <div className="text-center py-8 text-slate-500">Loading wishlist...</div>;
  if (!products.length) return (
    <div className="text-center py-12 bg-white rounded-lg border" data-testid="wishlist-tab">
      <Heart className="mx-auto text-slate-300 mb-3" size={48} />
      <p className="text-slate-500 mb-4">Your wishlist is empty</p>
      <Link to="/products"><Button className="bg-[#003366]">Explore Products</Button></Link>
    </div>
  );

  return (
    <div data-testid="wishlist-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {products.map(product => (
          <div key={product.id} className="relative">
            <ProductCard product={product} />
            <button
              onClick={() => handleRemove(product.id)}
              className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white shadow flex items-center justify-center text-red-500 hover:bg-red-50"
              data-testid={`remove-wishlist-${product.id}`}
            >
              <XIcon size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Account() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading, setUser, logout, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');

  // Handle Google OAuth callback
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      if (sessionId) {
        authGoogle({ session_id: sessionId })
          .then(res => {
            setUser(res.data);
            window.location.hash = '';
            toast.success('Signed in with Google!');
          })
          .catch(() => {
            toast.error('Google sign-in failed');
            navigate('/login');
          });
      }
    }
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Signed out');
  };

  if (loading) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><p className="text-slate-500">Loading...</p></div>;

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8" data-testid="account-page">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-slate-900">My Account</h1>
          <Button variant="outline" onClick={handleLogout} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="logout-btn">
            <LogOut size={16} className="mr-2" /> Sign Out
          </Button>
        </div>

        <div className="flex gap-6">
          {/* Sidebar tabs */}
          <nav className="hidden md:block w-48 flex-shrink-0">
            <div className="bg-white rounded-lg border overflow-hidden">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab.id ? 'bg-[#003366] text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon size={18} /> {tab.label}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Mobile tabs */}
          <div className="md:hidden flex gap-2 mb-4 overflow-x-auto pb-1 absolute w-[calc(100%-2rem)]" style={{ top: '8rem' }}>
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab.id ? 'bg-[#003366] text-white' : 'bg-white text-slate-600 border'
                  }`}
                >
                  <Icon size={16} /> {tab.label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1">
            {activeTab === 'profile' && <ProfileTab user={user} refreshUser={refreshUser} />}
            {activeTab === 'orders' && <OrdersTab />}
            {activeTab === 'addresses' && <AddressesTab />}
            {activeTab === 'wishlist' && <WishlistTab />}
          </div>
        </div>
      </div>
    </div>
  );
}
